import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle } from "lucide-react";
import HeroBreadcrumb from "@/components/layout/HeroBreadcrumb";

const BENEFICI = [
  { titolo: "Qualificazione Professionale", desc: "Ottenimento delle certificazioni necessarie per esercitare una professione specifica." },
  { titolo: "Conformità alle Normative", desc: "La formazione abilitante consente di rispettare le normative di settore, evitando sanzioni legali e operando in linea con le leggi vigenti." },
  { titolo: "Miglioramento della Competenza", desc: "Attraverso la formazione abilitante si acquisiscono nuove conoscenze e si migliorano le competenze già in possesso." },
  { titolo: "Investimento sul Lungo Periodo", desc: "Un modo per rafforzare la propria posizione professionale sul mercato, ottenendo un requisito formativo qualificato e a norma di legge." },
];

const PROGETTI_SPECIALI = [
  { titolo: "Programma GOL", desc: "Percorsi formativi brevi gratuiti per persone disoccupate e lavoratori in difficoltà, finanziati dal PNRR per qualificazione, riqualificazione o aggiornamento professionale." },
  { titolo: "Bando Acchiappa Talenti", desc: "L'iniziativa da 11 milioni di euro pensata per sostenere le imprese che assumono giovani, professionisti e alte competenze nel territorio regionale." },
  { titolo: "ARC - Corso per Agente di Commercio", desc: "Corso abilitante regolamentato dalla Regione Lazio per l'esercizio della professione di Agente e Rappresentante di Commercio su tutto il territorio nazionale. 120 ore, tra presenza e videoconferenza, con esame finale." },
  { titolo: "Accademia della Gastronomia", desc: "Percorso gratuito promosso da EBiT Lazio e Promo.Ter per formare la figura del Gastronomo, tra i pilastri del commercio di vicinato. 198 ore tra formazione teorica, verticalizzazioni su salumeria, caseificazione, pasticceria ed enologia, e stage on-the-job presso storiche realtà romane." },
  { titolo: "Corso SAB - Somministrazione e Vendita di Alimenti e Bevande", desc: "Corso abilitante, regolamentato dalla Regione Lazio, requisito obbligatorio per l'apertura di attività di somministrazione o vendita di alimenti e bevande. Comprende anche la formazione in igiene degli alimenti (HACCP). 120 ore, con doppia attestazione finale." },
  { titolo: "AIM - Corso Agenti Immobiliari", desc: "Corso abilitante regolamentato dalla Regione Lazio, requisito per l'iscrizione all'esame camerale che abilita alla professione di Agente Immobiliare. 150 ore, realizzato in collaborazione con FIMAA Roma, con un modulo dedicato agli aspetti pratici della professione." },
  { titolo: "Futuro Sicuro", desc: "Corso di alta formazione dedicato al contrasto della violenza di genere, rivolto a imprese, istituzioni e operatori del settore. Percorso multidisciplinare in FAD, accreditato dagli Ordini degli Avvocati e degli Assistenti Sociali." },
  { titolo: "Progetto Moda", desc: "Percorso gratuito promosso con EBiT Lazio per formare addetti alle vendite nel settore moda e retail, con formazione teorica e pratica \"on the job\" presso i migliori negozi del settore." },
];

const BENEFICI_DIRETTI = [
  "Certificazioni Riconosciute per svolgere attività regolamentate",
  "Conformità Legale: rispetto di tutte le normative settoriali",
  "Sviluppo delle Competenze per gestire ruoli altamente specializzati",
  "Supporto Finanziario: accesso ai fondi per ridurre il costo della formazione",
];

export default function FormazioneAbilitantePage() {
  return (
    <main id="main-content" data-testid="formazione-abilitante-page">
      <section className="relative h-[300px] md:h-[360px] overflow-hidden">
        <img src="https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=1920&q=60&auto=format" alt="Formazione abilitante e progetti speciali" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(4, 30, 66, 0.85)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative h-full flex flex-col justify-end pb-10">
          <HeroBreadcrumb />
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed'] mb-2">Formazione</span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight">Formazione Abilitante e Progetti Speciali</h1>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-lg text-[var(--confcom-text-primary)] font-semibold font-['Encode_Sans_Semi_Condensed'] leading-relaxed mb-5">
            Promo.Ter promuove corsi e progetti speciali rivolti a imprese, professionisti e persone in cerca di occupazione, spaziando da percorsi abilitanti e specialistici a bandi e iniziative finanziate per la qualificazione professionale e lo sviluppo del territorio.
          </p>
          <p className="text-[var(--confcom-text-secondary)] leading-relaxed">
            Affianchiamo i partecipanti in ogni fase, dall'accesso al bando o al corso fino al conseguimento della qualifica o dell'abilitazione richiesta.
          </p>
        </div>
      </section>

      <section className="py-16 bg-[#EBF5FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-8">I Benefici della Formazione Abilitante</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {BENEFICI.map((b) => (
              <div key={b.titolo} className="bg-white rounded-xl p-6 border border-[var(--confcom-border)]">
                <h3 className="text-base font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-2">{b.titolo}</h3>
                <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-8">Corsi e Progetti Speciali</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROGETTI_SPECIALI.map((p) => (
              <div key={p.titolo} className="bg-[var(--confcom-bg-subtle)] rounded-xl p-6 border border-[var(--confcom-border)]">
                <h3 className="text-base font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-2">{p.titolo}</h3>
                <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#EBF5FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto bg-white rounded-2xl p-8 border border-[var(--confcom-border)]">
            <h3 className="text-lg font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-5">Benefici Diretti per la Tua Professione</h3>
            <div className="space-y-3">
              {BENEFICI_DIRETTI.map((b) => (
                <div key={b} className="flex items-start gap-3">
                  <CheckCircle size={18} className="text-[var(--confcom-blue)] shrink-0 mt-0.5" />
                  <span className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed">{b}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[var(--confcom-blue)] rounded-2xl px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-5">
            <div>
              <p className="text-white font-bold text-xl font-['Encode_Sans_Semi_Condensed'] mb-2">Vuoi attivare subito il tuo percorso formativo o accedere a nuove opportunità? Contattaci!</p>
              <div className="text-white/75 text-sm space-y-1">
                <p>
                  <a href="mailto:commerciale.promoter@confcommercioroma.it" className="hover:text-white transition-colors">commerciale.promoter@confcommercioroma.it</a>
                  {" · "}
                  <a href="mailto:areagestione@confcommercioroma.it" className="hover:text-white transition-colors">areagestione@confcommercioroma.it</a>
                </p>
                <p>
                  <a href="tel:+390668437606" className="hover:text-white transition-colors">0668437 606</a>
                  {" · "}
                  <a href="tel:+390668437530" className="hover:text-white transition-colors">0668437 530</a>
                  {" · "}
                  <a href="tel:+390668437610" className="hover:text-white transition-colors">0668437 610</a>
                </p>
              </div>
            </div>
            <a href="https://forms.gle/98CnoTiUAdGHJ9e88" target="_blank" rel="noopener noreferrer" className="shrink-0 inline-flex items-center gap-2 bg-white text-[var(--confcom-blue)] px-7 py-3 rounded-md font-bold text-sm hover:opacity-90 transition-opacity whitespace-nowrap">
              Richiedi maggiori informazioni <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
